import React, { memo, useState, useRef, useEffect, useCallback } from 'react';
import { getFieldType, CUSTOM_FIELD_TYPES } from '../../../../config/customFieldTypes';
import './CustomFieldsSidebarSection.css';

/**
 * CustomFieldsSidebarSection Component
 * 
 * Renders all custom fields for a task in the detail sidebar.
 * Each field is displayed in a row with its label and an editable value.
 */
const CustomFieldsSidebarSection = memo(({ task, customFields, onCustomFieldValueChange }) => {
  if (!customFields || customFields.length === 0) {
    return null;
  }

  return (
    <div className="sidebar-custom-fields">
      <div className="sidebar-custom-fields__header">
        <h3 className="sidebar-custom-fields__title">Custom Fields</h3>
      </div>
      <div className="sidebar-custom-fields__list">
        {customFields.map(field => (
          <CustomFieldRow
            key={field.id}
            field={field}
            value={task?.customFieldValues?.[field.id]}
            taskId={task?.id}
            onValueChange={onCustomFieldValueChange}
          />
        ))}
      </div>
    </div>
  );
});

CustomFieldsSidebarSection.displayName = 'CustomFieldsSidebarSection';

/**
 * CustomFieldRow - renders a single custom field with label and editable value
 */
const CustomFieldRow = memo(({ field, value, taskId, onValueChange }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [localValue, setLocalValue] = useState(value ?? '');
  const inputRef = useRef(null);
  const fieldType = getFieldType(field.type);

  // Sync local value when value changes externally
  useEffect(() => {
    if (!isEditing) {
      setLocalValue(value ?? '');
    }
  }, [value, isEditing]);

  // Focus input when editing starts
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      if (inputRef.current.select) {
        inputRef.current.select();
      }
    }
  }, [isEditing]);

  // Handle save
  const handleSave = useCallback(() => {
    if (onValueChange && taskId) {
      onValueChange(taskId, field.id, localValue);
    }
    setIsEditing(false);
  }, [taskId, field.id, localValue, onValueChange]);

  // Handle key down
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      setLocalValue(value ?? '');
      setIsEditing(false);
    }
  }, [handleSave, value]);

  // Handle checkbox toggle
  const handleCheckboxClick = useCallback(() => {
    if (onValueChange && taskId) {
      onValueChange(taskId, field.id, !value);
    }
  }, [taskId, field.id, value, onValueChange]);

  // Render value based on field type
  const renderValue = () => {
    // Checkbox type
    if (field.type === 'checkbox') {
      const isChecked = value === true;
      return (
        <button
          className={`sidebar-custom-fields__checkbox ${isChecked ? 'sidebar-custom-fields__checkbox--checked' : ''}`}
          onClick={handleCheckboxClick}
        >
          {isChecked && (
            <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="2,6 5,9 10,3" />
            </svg>
          )}
        </button>
      );
    }

    // Select type
    if (field.type === 'select') {
      if (isEditing) {
        return (
          <select
            ref={inputRef}
            className="sidebar-custom-fields__select"
            value={localValue || ''}
            onChange={(e) => {
              const newValue = e.target.value || null;
              setLocalValue(newValue);
              if (onValueChange && taskId) {
                onValueChange(taskId, field.id, newValue);
              }
              setIsEditing(false);
            }}
            onBlur={() => setIsEditing(false)}
            onKeyDown={handleKeyDown}
          >
            <option value="">— Select —</option>
            {field.options?.map(opt => (
              <option key={opt.id} value={opt.id}>{opt.label}</option>
            ))}
          </select>
        );
      }

      const selectedOption = field.options?.find(opt => opt.id === value);
      return (
        <button
          className="sidebar-custom-fields__value-btn"
          onClick={() => setIsEditing(true)}
        >
          {selectedOption ? (
            <span 
              className="sidebar-custom-fields__select-pill"
              style={{ 
                backgroundColor: `${selectedOption.color}20`,
                color: selectedOption.color,
              }}
            >
              {selectedOption.label}
            </span>
          ) : (
            <span className="sidebar-custom-fields__empty">—</span>
          )}
        </button>
      );
    }

    // Date type
    if (field.type === 'date') {
      if (isEditing) {
        return (
          <input
            ref={inputRef}
            type="date"
            className="sidebar-custom-fields__input"
            value={localValue || ''}
            onChange={(e) => setLocalValue(e.target.value)}
            onBlur={handleSave}
            onKeyDown={handleKeyDown}
          />
        );
      }

      const displayValue = value ? new Date(value).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : null;
      return (
        <button
          className="sidebar-custom-fields__value-btn"
          onClick={() => setIsEditing(true)}
        >
          {displayValue || <span className="sidebar-custom-fields__empty">—</span>}
        </button>
      );
    }

    // Number type
    if (field.type === 'number') {
      if (isEditing) {
        return (
          <input
            ref={inputRef}
            type="number"
            className="sidebar-custom-fields__input sidebar-custom-fields__input--number"
            value={localValue ?? ''}
            onChange={(e) => setLocalValue(e.target.value === '' ? null : Number(e.target.value))}
            onBlur={handleSave}
            onKeyDown={handleKeyDown}
            placeholder="0"
          />
        );
      }

      return (
        <button
          className="sidebar-custom-fields__value-btn"
          onClick={() => setIsEditing(true)}
        >
          {value !== undefined && value !== null ? (
            <span className="sidebar-custom-fields__value--number">
              {typeof value === 'number' ? value.toLocaleString() : value}
            </span>
          ) : (
            <span className="sidebar-custom-fields__empty">—</span>
          )}
        </button>
      );
    }

    // URL type
    if (field.type === 'url') {
      if (isEditing) {
        return (
          <input
            ref={inputRef}
            type="url"
            className="sidebar-custom-fields__input"
            value={localValue || ''}
            onChange={(e) => setLocalValue(e.target.value)}
            onBlur={handleSave}
            onKeyDown={handleKeyDown}
            placeholder="https://..."
          />
        );
      }

      if (value) {
        let displayUrl = value;
        try {
          const url = new URL(value);
          displayUrl = url.hostname.replace('www.', '');
        } catch {}
        
        return (
          <a 
            href={value}
            target="_blank"
            rel="noopener noreferrer"
            className="sidebar-custom-fields__link"
          >
            {displayUrl}
          </a>
        );
      }

      return (
        <button
          className="sidebar-custom-fields__value-btn"
          onClick={() => setIsEditing(true)}
        >
          <span className="sidebar-custom-fields__empty">—</span>
        </button>
      );
    }

    // Text type (default)
    if (isEditing) {
      return (
        <input
          ref={inputRef}
          type="text"
          className="sidebar-custom-fields__input"
          value={localValue || ''}
          onChange={(e) => setLocalValue(e.target.value)}
          onBlur={handleSave}
          onKeyDown={handleKeyDown}
          placeholder={fieldType?.placeholder || 'Enter text...'}
        />
      );
    }

    return (
      <button
        className="sidebar-custom-fields__value-btn"
        onClick={() => setIsEditing(true)}
      >
        {value ? (
          <span className="sidebar-custom-fields__value" title={value}>{value}</span>
        ) : (
          <span className="sidebar-custom-fields__empty">—</span>
        )}
      </button>
    );
  };

  return (
    <div className="sidebar-custom-fields__row">
      <span className="sidebar-custom-fields__label" title={field.name}>
        {field.name}
      </span>
      <div className="sidebar-custom-fields__value-container">
        {renderValue()}
      </div>
    </div>
  );
});

CustomFieldRow.displayName = 'CustomFieldRow';

export default CustomFieldsSidebarSection;
